import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authAPI, newsAPI } from '../services/api';
import './NewsFormPage.css';

const NewsFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    content_en: '',
    content_ar: '',
    status: 'published'
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }

    if (isEditMode) {
      loadNewsPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode, navigate]);

  const loadNewsPost = async () => {
    try {
      const post = await newsAPI.getNewsById(id);
      setFormData({
        title_en: post.title_en,
        title_ar: post.title_ar,
        content_en: post.content_en,
        content_ar: post.content_ar,
        status: post.status
      });
      if (post.image_url) {
        setImagePreview(`http://localhost:5000${post.image_url}`);
      }
    } catch (err) {
      setError('Failed to load news post');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title_en', formData.title_en);
      formDataToSend.append('title_ar', formData.title_ar);
      formDataToSend.append('content_en', formData.content_en);
      formDataToSend.append('content_ar', formData.content_ar);
      formDataToSend.append('status', formData.status);
      
      if (image) {
        formDataToSend.append('image', image);
      }

      if (isEditMode) {
        await newsAPI.updateNews(id, formDataToSend);
      } else {
        await newsAPI.createNews(formDataToSend);
      }

      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="news-form-page">
      <div className="form-header">
        <div className="container">
          <button 
            className="back-button"
            onClick={() => navigate('/admin/dashboard')}
          >
            ← Back to Dashboard
          </button>
          <h1>{isEditMode ? 'Edit News Post' : 'Create News Post'}</h1>
        </div>
      </div>

      <div className="form-content">
        <div className="container">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="news-form">
            <div className="form-section">
              <h2>English Content</h2>
              
              <div className="form-group">
                <label htmlFor="title_en">Title (English) *</label>
                <input
                  type="text"
                  id="title_en"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter English title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="content_en">Content (English) *</label>
                <textarea
                  id="content_en"
                  name="content_en"
                  value={formData.content_en}
                  onChange={handleInputChange}
                  required
                  rows="8"
                  placeholder="Enter English content"
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Arabic Content</h2>
              
              <div className="form-group">
                <label htmlFor="title_ar">Title (Arabic) *</label>
                <input
                  type="text"
                  id="title_ar"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleInputChange}
                  required
                  placeholder="أدخل العنوان بالعربية"
                  dir="rtl"
                />
              </div>

              <div className="form-group">
                <label htmlFor="content_ar">Content (Arabic) *</label>
                <textarea
                  id="content_ar"
                  name="content_ar"
                  value={formData.content_ar}
                  onChange={handleInputChange}
                  required
                  rows="8"
                  placeholder="أدخل المحتوى بالعربية"
                  dir="rtl"
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Image & Settings</h2>
              
              <div className="form-group">
                <label htmlFor="image">Featured Image</label>
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/admin/dashboard')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update Post' : 'Create Post')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsFormPage;
